// ============================================================
//  AiAgentPanel.js — Website Widget → AI agent
//
//  Per widget: when the AI answers, its name and rules, which of the
//  account's own AI keys it uses (AI Integrations), what it knows
//  (FAQs, website pages, documents) and a box to test it.
// ============================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { getKnowledge, knowledgeAction, uploadKnowledgeDoc, updateWidget } from '../../api/client';

const PROVIDER_LABEL = { openai: 'OpenAI', claude: 'Claude', gemini: 'Gemini' };
const MODES = [
  ['off', 'Off', 'Only your team answers.'],
  ['away', 'When I’m away', 'AI answers while you’re Away or CommandKit is closed.'],
  ['always', 'Always first', 'AI answers every visitor; you step in when needed.'],
];

function aiForm(w) {
  return {
    aiMode: w.aiMode || 'off', aiProvider: w.aiProvider || 'auto', aiName: w.aiName || 'AI assistant',
    aiInstructions: w.aiInstructions || '', aiHandoffTopics: w.aiHandoffTopics || '', aiMaxReplies: w.aiMaxReplies || 12,
  };
}

export default function AiAgentPanel({ widgets, onWidgetUpdated }) {
  const [selId, setSelId]       = useState(widgets[0]?.wId ?? null);
  const selected = useMemo(() => widgets.find(w => w.wId === selId) || widgets[0] || null, [widgets, selId]);
  const [form, setForm]         = useState(() => (selected ? aiForm(selected) : null));
  const [items, setItems]       = useState(null);
  const [providers, setProviders] = useState({});
  const [saving, setSaving]     = useState(false);
  const [faq, setFaq]           = useState(null);       // { id, question, answer } being edited
  const [url, setUrl]           = useState('');
  const [busy, setBusy]         = useState('');         // 'url' | 'discover' | 'doc' | 'test' | 'import'
  const [found, setFound]       = useState(null);       // { urls[], picked{} } from "Import from my site"
  const [progress, setProgress] = useState(null);       // { done, total }
  const [question, setQuestion] = useState('');
  const [answer, setAnswer]     = useState(null);
  const [delAsk, setDelAsk]     = useState(null);
  const docRef = useRef(null);

  useEffect(() => { if (selected) { setForm(aiForm(selected)); setAnswer(null); setFound(null); setFaq(null); } }, [selected?.wId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!selected) return;
    try {
      const res = await getKnowledge(selected.wId);
      setItems(res.data.items || []);
      setProviders(res.data.providers || {});
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not load the AI knowledge'); }
  }, [selected]);
  useEffect(() => { setItems(null); load(); }, [load]);

  if (!selected || !form) return null;
  const connected = Object.keys(providers);
  const dirty = JSON.stringify(form) !== JSON.stringify(aiForm(selected));
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const counts = (items || []).reduce((a, i) => ({ ...a, [i.type]: (a[i.type] || 0) + 1 }), {});

  async function save() {
    setSaving(true);
    try {
      const res = await updateWidget(selected.wId, form);
      onWidgetUpdated(res.data.widget);
      setForm(aiForm(res.data.widget));
      toast.success(form.aiMode === 'off' ? 'AI agent is off' : 'AI agent saved');
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not save'); }
    setSaving(false);
  }

  async function saveFaq(e) {
    e.preventDefault();
    try {
      const res = await knowledgeAction('kb_faq', selected.wId, { kId: faq.id || 0, question: faq.question, answer: faq.answer });
      setItems(res.data.items); setFaq(null);
      toast.success('FAQ saved — the AI uses it straight away');
    } catch (err) { toast.error(err?.response?.data?.message || 'Could not save the FAQ'); }
  }

  async function remove(item) {
    if (delAsk !== item.id) { setDelAsk(item.id); return; }
    setDelAsk(null);
    try { const res = await knowledgeAction('kb_delete', selected.wId, { kId: item.id }); setItems(res.data.items); }
    catch (err) { toast.error(err?.response?.data?.message || 'Could not remove it'); }
  }

  async function addUrl(e, target, kId) {
    e?.preventDefault();
    const u = (target ?? url).trim();
    if (!u) return;
    setBusy('url');
    try {
      const res = await knowledgeAction('kb_url', selected.wId, { url: u, kId: kId || 0 });
      setItems(res.data.items); if (!target) setUrl('');
      toast.success(kId ? 'Page refreshed' : 'Page added');
    } catch (err) { toast.error(err?.response?.data?.message || 'Could not read that page'); }
    setBusy('');
  }

  async function discover() {
    const u = url.trim();
    if (!u) { toast.error('Type your website address first'); return; }
    setBusy('discover');
    try {
      const res = await knowledgeAction('kb_discover', selected.wId, { url: u });
      const urls = res.data.urls || [];
      if (!urls.length) toast.error('No pages found on that site');
      setFound({ urls, picked: Object.fromEntries(urls.map(x => [x, true])) });
    } catch (err) { toast.error(err?.response?.data?.message || 'Could not read that site'); }
    setBusy('');
  }

  async function importPicked() {
    const list = found.urls.filter(x => found.picked[x]);
    setBusy('import'); setProgress({ done: 0, total: list.length });
    let ok = 0; let lastItems = null;
    for (let i = 0; i < list.length; i++) {
      try { const res = await knowledgeAction('kb_url', selected.wId, { url: list[i] }); lastItems = res.data.items; ok++; }
      catch { /* skip pages that fail; the count below says how many worked */ }
      setProgress({ done: i + 1, total: list.length });
    }
    if (lastItems) setItems(lastItems);
    setBusy(''); setProgress(null); setFound(null); setUrl('');
    toast.success(`${ok} of ${list.length} pages added`);
  }

  async function onDoc(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Documents must be under 8 MB'); return; }
    setBusy('doc');
    try { const res = await uploadKnowledgeDoc(selected.wId, file); setItems(res.data.items); toast.success('Document added'); }
    catch (err) { toast.error(err?.response?.data?.message || 'Could not read that document'); }
    setBusy('');
  }

  async function test(e) {
    e.preventDefault();
    if (!question.trim()) return;
    setBusy('test'); setAnswer(null);
    try { const res = await knowledgeAction('ai_test', selected.wId, { question }); setAnswer(res.data); }
    catch (err) { toast.error(err?.response?.data?.message || 'The test failed'); }
    setBusy('');
  }

  const faqs  = (items || []).filter(i => i.type === 'faq');
  const pages = (items || []).filter(i => i.type === 'page');
  const docs  = (items || []).filter(i => i.type === 'doc');

  return (
    <div className="aia">
      {widgets.length > 1 && (
        <div className="aia-pick" role="tablist" aria-label="Widget">
          {widgets.map(w => (
            <button key={w.wId} type="button" role="tab" aria-selected={w.wId === selected.wId} className={w.wId === selected.wId ? 'is-on' : ''} onClick={() => setSelId(w.wId)}>
              <i style={{ background: w.color }} /> {w.label}
              {w.aiMode !== 'off' && <em>AI on</em>}
            </button>
          ))}
        </div>
      )}

      {!connected.length && items !== null && (
        <div className="aia-connect">
          <span><i className="fa-solid fa-key" /></span>
          <div>
            <b>Connect your AI key first</b>
            <p>The AI agent runs on your own OpenAI, Claude or Gemini account, so you control the cost. Add a key once in AI Integrations — it’s stored encrypted.</p>
          </div>
          <Link to="/integrations" className="wwg-btn wwg-btn-dark wwg-btn-sm">Open AI Integrations</Link>
        </div>
      )}

      <div className="aia-grid">
        {/* ── left: settings + knowledge ── */}
        <div className="wwg-col">
          <section className="wwg-card">
            <div className="wwg-card-row">
              <h3>AI agent for {selected.label}</h3>
              {form.aiMode !== 'off' && connected.length > 0 && <span className="wwg-tag is-live">Active</span>}
            </div>
            <div className="aia-modes" role="radiogroup" aria-label="When the AI answers">
              {MODES.map(([v, l, d]) => (
                <button key={v} type="button" role="radio" aria-checked={form.aiMode === v} className={form.aiMode === v ? 'is-on' : ''} onClick={() => set('aiMode', v)}>
                  <b>{l}</b><span>{d}</span>
                </button>
              ))}
            </div>
            <div className="wwg-two">
              <label className="wwg-field">
                <span>AI name visitors see</span>
                <input value={form.aiName} onChange={e => set('aiName', e.target.value)} maxLength={40} placeholder="AI assistant" />
              </label>
              <label className="wwg-field">
                <span>AI account</span>
                <select value={form.aiProvider} onChange={e => set('aiProvider', e.target.value)}>
                  <option value="auto">{connected.length ? `Automatic (${PROVIDER_LABEL[connected[0]]})` : 'Automatic'}</option>
                  {['openai', 'claude', 'gemini'].map(p => (
                    <option key={p} value={p} disabled={!providers[p]}>{PROVIDER_LABEL[p]}{providers[p] ? ` · ${providers[p]}` : ' — not connected'}</option>
                  ))}
                </select>
              </label>
            </div>
            <label className="wwg-field">
              <span>Instructions for the AI <em className="aia-opt">(optional)</em></span>
              <textarea rows={3} value={form.aiInstructions} onChange={e => set('aiInstructions', e.target.value)} maxLength={1500}
                placeholder="e.g. Be warm and brief. Always mention that delivery is instant. Never promise refunds — hand those to a person." />
            </label>
            <div className="wwg-two">
              <label className="wwg-field">
                <span>Always hand these topics to a person</span>
                <input value={form.aiHandoffTopics} onChange={e => set('aiHandoffTopics', e.target.value)} maxLength={500} placeholder="refund, complaint, chargeback" />
                <em>Comma-separated words. If a visitor mentions one, a person takes over.</em>
              </label>
              <label className="wwg-field">
                <span>Max AI replies per chat</span>
                <input type="number" min={1} max={50} value={form.aiMaxReplies} onChange={e => set('aiMaxReplies', Number(e.target.value) || 1)} />
                <em>After this many, the chat goes to your team — keeps your AI bill in check.</em>
              </label>
            </div>
            <div className="aia-rules">
              <i className="fa-solid fa-shield-halved" />
              <span>The AI only answers from your knowledge below, replies in the visitor’s language, never invents prices or policies, and hands off to a person when it isn’t sure, on request, or when a photo is sent. Each handoff gives you a short summary and the usual chime.</span>
            </div>
            <div className="aia-save">
              <button type="button" className="wwg-btn wwg-btn-brand" onClick={save} disabled={!dirty || saving}>
                {saving ? <span className="wwg-spin" /> : <i className="fa-solid fa-check" />} Save AI settings
              </button>
              {dirty && <button type="button" className="wwg-btn wwg-btn-ghost" onClick={() => setForm(aiForm(selected))} disabled={saving}>Discard</button>}
            </div>
          </section>

          <section className="wwg-card">
            <div className="wwg-card-row">
              <div>
                <h3>Knowledge</h3>
                <p className="wwg-sub">What the AI is allowed to answer from. FAQs are the most reliable.</p>
              </div>
              <span className="aia-counts">{counts.faq || 0} FAQs · {counts.page || 0} pages · {counts.doc || 0} docs</span>
            </div>

            {/* FAQs */}
            <div className="aia-block">
              <div className="aia-block-head">
                <b><i className="fa-regular fa-circle-question" /> FAQs</b>
                {!faq && <button type="button" className="wwg-btn wwg-btn-ghost wwg-btn-sm" onClick={() => setFaq({ id: 0, question: '', answer: '' })}><i className="fa-solid fa-plus" /> Add FAQ</button>}
              </div>
              {faq && (
                <form className="aia-faq-form" onSubmit={saveFaq}>
                  <input autoFocus value={faq.question} onChange={e => setFaq({ ...faq, question: e.target.value })} placeholder="Question, e.g. How long does delivery take?" maxLength={300} aria-label="Question" />
                  <textarea rows={3} value={faq.answer} onChange={e => setFaq({ ...faq, answer: e.target.value })} placeholder="Answer, e.g. Data is delivered instantly — usually within a minute." maxLength={4000} aria-label="Answer" />
                  <div>
                    <button type="submit" className="wwg-btn wwg-btn-dark wwg-btn-sm" disabled={!faq.question.trim() || !faq.answer.trim()}>Save FAQ</button>
                    <button type="button" className="wwg-btn wwg-btn-ghost wwg-btn-sm" onClick={() => setFaq(null)}>Cancel</button>
                  </div>
                </form>
              )}
              {items === null ? <div className="aia-empty"><span className="wwg-spin" /></div> : faqs.length === 0 && !faq ? (
                <div className="aia-empty">No FAQs yet. Add the 5–10 questions customers ask most.</div>
              ) : (
                <ul className="aia-list">
                  {faqs.map(i => (
                    <li key={i.id}>
                      <div><b>{i.title}</b><span>{i.preview}</span></div>
                      <button type="button" className="wwg-icon-btn" aria-label="Edit FAQ" onClick={() => setFaq({ id: i.id, question: i.title, answer: i.preview })}><i className="fa-solid fa-pen" /></button>
                      <button type="button" className={`wwg-icon-btn ${delAsk === i.id ? 'is-bad' : ''}`} aria-label={delAsk === i.id ? 'Tap again to remove' : 'Remove FAQ'} title={delAsk === i.id ? 'Tap again to remove' : 'Remove'} onClick={() => remove(i)} onBlur={() => setDelAsk(null)}><i className="fa-solid fa-trash-can" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Website pages */}
            <div className="aia-block">
              <div className="aia-block-head"><b><i className="fa-solid fa-globe" /> Website pages</b></div>
              <form className="aia-url" onSubmit={e => addUrl(e)}>
                <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://yoursite.com/pricing" aria-label="Page address" disabled={!!busy} />
                <button type="submit" className="wwg-btn wwg-btn-dark wwg-btn-sm" disabled={!url.trim() || !!busy}>{busy === 'url' ? <span className="wwg-spin" /> : 'Add page'}</button>
                <button type="button" className="wwg-btn wwg-btn-ghost wwg-btn-sm" onClick={discover} disabled={!url.trim() || !!busy} title="Find the pages on this website">
                  {busy === 'discover' ? <span className="wwg-spin" /> : <><i className="fa-solid fa-sitemap" /> Import site</>}
                </button>
              </form>
              {found && (
                <div className="aia-found">
                  <div className="aia-found-head">
                    <b>{found.urls.length} pages found — choose which to add</b>
                    <button type="button" className="aia-link" onClick={() => setFound(f => ({ ...f, picked: Object.fromEntries(f.urls.map(x => [x, !Object.values(f.picked).every(Boolean)])) }))}>Toggle all</button>
                  </div>
                  <ul>
                    {found.urls.map(u => (
                      <li key={u}><label><input type="checkbox" checked={!!found.picked[u]} onChange={e => setFound(f => ({ ...f, picked: { ...f.picked, [u]: e.target.checked } }))} /> {u.replace(/^https?:\/\//, '')}</label></li>
                    ))}
                  </ul>
                  <div className="aia-found-foot">
                    {progress ? <span className="aia-progress"><span style={{ width: `${(progress.done / progress.total) * 100}%` }} /> {progress.done} / {progress.total}</span> : <span />}
                    <div>
                      <button type="button" className="wwg-btn wwg-btn-ghost wwg-btn-sm" onClick={() => setFound(null)} disabled={busy === 'import'}>Cancel</button>
                      <button type="button" className="wwg-btn wwg-btn-dark wwg-btn-sm" onClick={importPicked} disabled={busy === 'import' || !Object.values(found.picked).some(Boolean)}>
                        Add {Object.values(found.picked).filter(Boolean).length} pages
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {pages.length > 0 && (
                <ul className="aia-list">
                  {pages.map(i => (
                    <li key={i.id}>
                      <div><b>{i.title}</b><span><a href={i.source} target="_blank" rel="noopener noreferrer">{i.source.replace(/^https?:\/\//, '')}</a> · {Math.round(i.chars / 100) / 10}k characters</span></div>
                      <button type="button" className="wwg-icon-btn" aria-label="Refresh page" title="Read the page again" onClick={e => addUrl(e, i.source, i.id)} disabled={!!busy}><i className="fa-solid fa-rotate" /></button>
                      <button type="button" className={`wwg-icon-btn ${delAsk === i.id ? 'is-bad' : ''}`} aria-label={delAsk === i.id ? 'Tap again to remove' : 'Remove page'} onClick={() => remove(i)} onBlur={() => setDelAsk(null)}><i className="fa-solid fa-trash-can" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Documents */}
            <div className="aia-block">
              <div className="aia-block-head">
                <b><i className="fa-regular fa-file-lines" /> Documents</b>
                <label className={`wwg-btn wwg-btn-ghost wwg-btn-sm ${busy ? 'is-disabled' : ''}`}>
                  <input ref={docRef} type="file" accept=".pdf,.docx,.txt,.md,.csv" hidden onChange={onDoc} disabled={!!busy} />
                  {busy === 'doc' ? <span className="wwg-spin" /> : <i className="fa-solid fa-upload" />} Upload
                </label>
              </div>
              <p className="aia-hint">PDF, Word (.docx), TXT, Markdown or CSV, up to 8 MB — price lists, policies, guides. Scanned PDFs (pictures of text) can’t be read.</p>
              {docs.length > 0 && (
                <ul className="aia-list">
                  {docs.map(i => (
                    <li key={i.id}>
                      <div><b>{i.title}</b><span>{i.preview}</span></div>
                      <button type="button" className={`wwg-icon-btn ${delAsk === i.id ? 'is-bad' : ''}`} aria-label={delAsk === i.id ? 'Tap again to remove' : 'Remove document'} onClick={() => remove(i)} onBlur={() => setDelAsk(null)}><i className="fa-solid fa-trash-can" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>

        {/* ── right: test ── */}
        <div className="wwg-col">
          <section className="wwg-card aia-test">
            <h3><i className="fa-solid fa-flask" /> Test the AI</h3>
            <p className="wwg-sub">Ask what a customer would ask. Nothing is sent to anyone.</p>
            <form onSubmit={test} className="aia-test-form">
              <input value={question} onChange={e => setQuestion(e.target.value)} placeholder="e.g. How much is MTN 2GB?" maxLength={500} aria-label="Test question" />
              <button type="submit" className="wwg-btn wwg-btn-brand wwg-btn-sm" disabled={!question.trim() || busy === 'test' || !connected.length}>
                {busy === 'test' ? <span className="wwg-spin" /> : 'Ask'}
              </button>
            </form>
            {answer && (
              <div className={`aia-answer ${answer.handoff ? 'is-handoff' : ''}`}>
                {answer.handoff ? (
                  <>
                    <b><i className="fa-solid fa-headset" /> Would hand this to a person</b>
                    <p>{answer.reply || 'The answer isn’t in your knowledge yet.'}</p>
                    <em>Tip: add an FAQ for this question so the AI can answer it next time.</em>
                  </>
                ) : (
                  <>
                    <b><span className="aia-badge">AI</span> {form.aiName || 'AI assistant'}</b>
                    <p>{answer.reply}</p>
                  </>
                )}
                {answer.sources?.length > 0 && <span className="aia-src">Used: {answer.sources.join(' · ')}</span>}
                <span className="aia-src">{answer.provider}</span>
              </div>
            )}
          </section>
          <section className="wwg-card aia-how">
            <h3>How it works for visitors</h3>
            <ol>
              <li>The visitor writes — the AI replies in seconds, with an <span className="aia-badge">AI</span> label.</li>
              <li>A <b>Talk to a person</b> button is always there.</li>
              <li>When the AI hands off, you hear the chime and see <b>Needs you</b> in Chats, with a summary at the top.</li>
              <li>As soon as you reply, the AI steps back for that chat.</li>
            </ol>
          </section>
        </div>
      </div>
    </div>
  );
}
